import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, UseMutationResult } from "@tanstack/react-query";
import { handleApiError, handleApiSuccess } from "@/utils/errorHandler";
import type { ApiError } from "@/utils/errorHandler";
import i18n from "@/i18n/config";

/**
 * Query key used by a cache action: either a static array or one derived from
 * the mutation result and variables at success time.
 */
export type CrudQueryKey<TData, TVariables> =
  | readonly unknown[]
  | ((data: TData, variables: TVariables) => readonly unknown[]);

export interface CrudCacheAction<TData, TVariables> {
  /** Cache operation to perform. */
  type: "invalidate" | "setQueryData" | "removeQueries";
  /** Query key to act on; "setQueryData" caches the mutation result. */
  key: CrudQueryKey<TData, TVariables>;
}

export interface CrudSuccessContext<TData, TVariables> {
  queryClient: QueryClient;
  data: TData;
  variables: TVariables;
}

export interface CrudOperationConfig<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Ordered cache operations executed on success. */
  cacheActions?: CrudCacheAction<TData, TVariables>[];
  /** i18n key of the success toast (shown via handleApiSuccess). */
  successMessageKey?: string;
  /** i18n key of the error toast fallback (shown via handleApiError). */
  errorMessageKey?: string;
  /** Extra success behavior run after cacheActions and the success toast. */
  onSuccess?: (context: CrudSuccessContext<TData, TVariables>) => void;
  /** Custom error handling; replaces the default handleApiError flow. */
  onError?: (error: unknown) => void;
}

type MutationHook<TData, TVariables> = () => UseMutationResult<
  TData,
  Error,
  TVariables
>;

/**
 * Hooks returned by createCrudHooks. An operation whose type parameters stay
 * `never` (i.e. was not configured) yields `undefined` for its hook.
 */
export type CrudHooks<
  TCreateData,
  TCreateVars,
  TUpdateData,
  TUpdateVars,
  TDeleteData,
  TDeleteVars,
> = {
  useCreate: [TCreateData, TCreateVars] extends [never, never]
    ? undefined
    : MutationHook<TCreateData, TCreateVars>;
  useUpdate: [TUpdateData, TUpdateVars] extends [never, never]
    ? undefined
    : MutationHook<TUpdateData, TUpdateVars>;
  useDelete: [TDeleteData, TDeleteVars] extends [never, never]
    ? undefined
    : MutationHook<TDeleteData, TDeleteVars>;
};

function applyCacheActions<TData, TVariables>(
  queryClient: QueryClient,
  actions: CrudCacheAction<TData, TVariables>[],
  data: TData,
  variables: TVariables,
): void {
  for (const action of actions) {
    const key =
      typeof action.key === "function"
        ? action.key(data, variables)
        : action.key;
    switch (action.type) {
      case "invalidate":
        queryClient.invalidateQueries({ queryKey: key });
        break;
      case "setQueryData":
        queryClient.setQueryData(key, data);
        break;
      case "removeQueries":
        queryClient.removeQueries({ queryKey: key });
        break;
    }
  }
}

function buildMutationHook<TData, TVariables>({
  mutationFn,
  cacheActions = [],
  successMessageKey,
  errorMessageKey,
  onSuccess,
  onError,
}: CrudOperationConfig<TData, TVariables>): MutationHook<TData, TVariables> {
  return () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn,
      onSuccess: (data, variables) => {
        applyCacheActions(queryClient, cacheActions, data, variables);
        if (successMessageKey) {
          handleApiSuccess(i18n.t(successMessageKey));
        }
        onSuccess?.({ queryClient, data, variables });
      },
      onError: (error) => {
        if (onError) {
          onError(error);
        } else if (errorMessageKey) {
          handleApiError(error as ApiError, i18n.t(errorMessageKey));
        }
      },
    });
  };
}

/**
 * Builds { useCreate, useUpdate, useDelete } hooks for one entity. Operations
 * that are not configured (e.g. laps have no create) yield `undefined`.
 */
export function createCrudHooks<
  TCreateData = never,
  TCreateVars = never,
  TUpdateData = never,
  TUpdateVars = never,
  TDeleteData = never,
  TDeleteVars = never,
>(config: {
  create?: CrudOperationConfig<TCreateData, TCreateVars>;
  update?: CrudOperationConfig<TUpdateData, TUpdateVars>;
  delete?: CrudOperationConfig<TDeleteData, TDeleteVars>;
}): CrudHooks<
  TCreateData,
  TCreateVars,
  TUpdateData,
  TUpdateVars,
  TDeleteData,
  TDeleteVars
> {
  return {
    useCreate: config.create ? buildMutationHook(config.create) : undefined,
    useUpdate: config.update ? buildMutationHook(config.update) : undefined,
    useDelete: config.delete ? buildMutationHook(config.delete) : undefined,
  } as CrudHooks<
    TCreateData,
    TCreateVars,
    TUpdateData,
    TUpdateVars,
    TDeleteData,
    TDeleteVars
  >;
}
